import type { Config, } from "wagmi";
import { getViemClients, } from "@bracket/contractor-next";

import {
	bigintize,
	getContractWithABI,
	getTokenDefinition,
	type TokenIdentifier,
	type ValidChainId,
} from "@bracket/chainography";

export declare type CalculateMintProps = {
	token: TokenIdentifier;
	amount: bigint|number;
	chain?: ValidChainId;
	chainId?: ValidChainId;
	config?: Config;
};




/**
 * Calculates the amount of brktETH tokens that will be minted for a given input amount and token
 * @summary For collateral tokens, calculates the mint amount that will be received for a given input amount
 * @param {Object} params - The parameters for calculating mint amount
 * @param {TokenIdentifier} params.token - The collateral token being used for this mint
 * @param {number} params.amount - The amount of collateral to mint with
 * @param {ValidChainId} [params.chainId=1] - The chain ID to use (defaults to 1)
 * @param {ValidChainId} [params.chain] - Deprecated parameter, use chainId instead
 * @param {Config} [params.config] - Optional wagmi config for frontend usage
 * @returns {Promise<bigint>} The calculated brktETH amount to expect from this mint
 * @throws {Error} If the contract call fails or returns an invalid value
 */
export const calculateMint = async ({
	token,
	amount,
	chain,
	chainId = 1,
	config,
}: CalculateMintProps): Promise<bigint> => {

	try {

		if(chain) {
			console.warn("calculateMint: chain is deprecated, use chainId instead");
			chainId = chain;
		}

		if(chainId !== 1)
			throw new Error("mint supports only chain 1");

		const [vclient, _] = await getViemClients({ config, chainId, });
		const tok = getTokenDefinition(token, chainId);
		const [address, abi] = getContractWithABI("BrktETH", chainId);
		const biamount = bigintize(amount);
		const args = ["ETH", "WETH"].includes(tok.symbol) ? [biamount] : [tok.address, biamount];

		const result = await vclient.readContract({
			address,
			abi,
			functionName: "calculateMint",
			args,
		});

		if(typeof result === "bigint") {
			return result;
		}

		throw new Error("contract did not return the expected value as a bigint");

	} catch (err) {

		throw new Error(`@bracket/chainography/brktETH: ${err.message}`);
	}
};
